import React, {useState, useEffect, useRef} from 'react';
import {useHistory, useParams} from "react-router-dom";
import {withTranslation} from 'react-i18next';
import {DataTable} from 'primereact/datatable';
import {Column} from 'primereact/column';
import {Button} from "primereact/button";
import {Dropdown} from "primereact/dropdown";
import axios from "axios";
import {Dialog} from "primereact/dialog";
import {InputText} from "primereact/inputtext";

const DIRECTIONAL_RECEIPT_RELATION_TYPES = new Set([
  'RECEIPT_DERIVATION',
  'ADVANCE_SETTLEMENT',
]);

const getReceiptRelationDescriptionKey = (type, direction) => {
  const normalizedType = type || 'MANUAL_RELATED_RECEIPT';
  const variant = DIRECTIONAL_RECEIPT_RELATION_TYPES.has(normalizedType)
    ? (direction === 'INCOMING' ? 'INCOMING' : 'OUTGOING')
    : 'DEFAULT';

  return `receiptRelationDescription.${normalizedType}.${variant}`;
};

const ReceiptRelationListComponent = (props) => {

  const {t} = props;
  const history = useHistory();
  const {id} = useParams();
  const [receiptRelations, setReceiptRelations] = useState([]);
  const [loading, setLoading] = useState(false);
  const receiptId = props?.receiptId ?? props?.initialValues?.id ?? id;
  const [totalRecords, setTotalRecords] = useState(0);
  const [rows, setRows] = useState(Number(process.env.REACT_APP_LIST_ROW_DEFAULT));
  const dt = useRef(null);
  const [showAddReceipt, setShowAddReceipt] = useState(false);
  const [showAddInvDoc, setShowAddInvDoc] = useState(false);

  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [selectedInvDoc, setSelectedInvDoc] = useState(null);
  const [browseType, setBrowseType] = useState(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [dialogError, setDialogError] = useState(null);

  const [receiptTypesDict, setReceiptTypesDict] = useState([]);
  const [invDocTypesDict, setInvDocTypesDict] = useState([]);

  const [lazyParams, setLazyParams] = useState({
    first: 0,
    rows: rows,
    page: 0,
    sortField: 'id',
    sortOrder: -1,
  });
  let loadLazyTimeout = null;
  const loadLazyData = () => {
    setLoading(true);
    if (loadLazyTimeout) {
      clearTimeout(loadLazyTimeout);
    }
    loadLazyTimeout = setTimeout(() => {
      apiCall('get', `/api/v2/receipt/${receiptId}/related-receipts`)
        .then(response => {
          setTotalRecords(response.data.data.total_item_count);
          setReceiptRelations(response.data.data.items)
        })
        .catch(error => {
          window.App.toastShow('error', t('error'), error.toString(), '', error.response);
        })
        .finally(() => {
          setLoading(false);
        })
    }, Math.random() * 1000 + 250);
  }
  const onPage = (event) => {
    let _lazyParams = {...lazyParams, ...event};
    setLazyParams(_lazyParams);
  }
  const onSort = (event) => {
    let _lazyParams = {...lazyParams, ...event};
    setLazyParams(_lazyParams);
  }
  const onFilter = (event) => {
    let _lazyParams = {...lazyParams, ...event};
    _lazyParams['first'] = 0;
    _lazyParams['page'] = 0;
    setLazyParams(_lazyParams);
  }
  const pager = {
    layout: 'RowsPerPageDropdown CurrentPageReport PrevPageLink NextPageLink',
    'RowsPerPageDropdown': (options) => {
      const dropdownOptions = [
        {label: Number(process.env.REACT_APP_LIST_ROW_OP1), value: Number(process.env.REACT_APP_LIST_ROW_OP1)},
        {label: Number(process.env.REACT_APP_LIST_ROW_OP2), value: Number(process.env.REACT_APP_LIST_ROW_OP2)},
        {label: Number(process.env.REACT_APP_LIST_ROW_OP3), value: Number(process.env.REACT_APP_LIST_ROW_OP3)},
        {label: Number(process.env.REACT_APP_LIST_ROW_OP4), value: Number(process.env.REACT_APP_LIST_ROW_OP4)},
      ];

      return (
        <>
          {/*<span className="p-mx-1" style={{color: 'var(--text-color)', userSelect: 'none'}}>Tételek egy oldalon: </span>*/}
          <Dropdown value={options.value} options={dropdownOptions} onChange={(e) => setRows(e.target.value)}
                    appendTo={document.body}/>
        </>
      );
    },
    'CurrentPageReport': (options) => {
      return (
        <span style={{color: 'var(--text-color)', userSelect: 'none', width: '120px', textAlign: 'center'}}>
                        {options.first} - {options.last} / {options.totalRecords}
        </span>
      )
    }
  };

  const apiCall = (method, url, data) => {
    return axios({
      method: method,
      url: process.env.REACT_APP_API_HOST + url,
      data,
      params: {},
      headers: {
        'Authorization': 'Bearer ' + localStorage.getItem("userToken"),
        'App-Network': localStorage.getItem("networkId")
      }
    })
  }

  const translateReceiptType = (key) => {
    if (!key) return '-';
    const found = (receiptTypesDict || []).find(x => x.id === key);
    return found ? found.name : key;
  };

  const translateInvDocType = (key) => {
    if (!key) return '-';
    const found = (invDocTypesDict || []).find(x => x.id === key);
    return found ? found.name : key;
  };

  const translateRelationDescription = (rowData) => {
    const key = getReceiptRelationDescriptionKey(rowData?.type, rowData?.direction);
    const relatedNumber = rowData?.related_receipt?.receipt_number
      || rowData?.inventory_document?.document_number
      || '';
    const projectLabel = [rowData?.project?.project_number, rowData?.project?.name]
      .filter(Boolean)
      .join(' – ') || t('receiptRelationDescription.UNKNOWN_PROJECT');

    return t(key, {
      relatedNumber,
      projectLabel,
      defaultValue: t('receiptRelationDescription.UNKNOWN'),
    });
  };

  const canViewRelationTarget = (rowData) => {
    if (rowData?.related_receipt?.id || rowData?.inventory_document?.id) return true;

    return Boolean(
      rowData?.project?.id
      && (localStorage.getItem('roles') || '').toLowerCase().includes('role_project_read')
    );
  };

  const openBrowse = (type) => {
    setBrowseType(type);
    const basePath = history.location.pathname;
    const params = new URLSearchParams(history.location.search);
    params.set('search', type === 'receipt' ? 'receiptRelationReceipt' : 'receiptRelationInvDoc');
    history.push(`${basePath}?${params.toString()}`);
  };

  const extractApiError = (error) => {
    const data = error?.response?.data;
    if (!data) return error?.toString?.() || 'Ismeretlen hiba';
    if (typeof data.detail === 'string') return data.detail;
    if (typeof data.message === 'string') return data.message;
    if (Array.isArray(data.violations) && data.violations.length) {
      return data.violations.map(v => `${v.propertyPath}: ${v.message}`).join('\n');
    }
    try { return JSON.stringify(data); } catch (e) {}
    return 'Hiba történt';
  };

  const createRelation = (mode) => {
    setDialogError(null);
    const parentId = Number(receiptId);
    if (!parentId) return;
    const payload = { parent: Number(receiptId) };
    if (mode === 'receipt') {
      const rid = Number(selectedReceipt?.id);
      if (!rid) { setDialogError(t('selectInvoiceWarning')); return; }
      payload.related_receipt = rid;
    }
    if (mode === 'invdoc') {
      const did = Number(selectedInvDoc?.id);
      if (!did) { setDialogError(t('selectDeliveryNoteWarning')); return; }
      payload.inventory_document = did;
    }
    setCreateLoading(true);
    apiCall('post', '/api/v2/receipt-relation', payload)
      .then(() => {
        window.App.toastShow('success', 'Sikeres mentés!');
        window.App.setState({rerenderReceiptRelationList: Math.random()});
        setSelectedReceipt(null);
        setSelectedInvDoc(null);
        setShowAddReceipt(false);
        setShowAddInvDoc(false);
      })
      .catch((error) => {
        setDialogError(extractApiError(error));
      })
      .finally(() => setCreateLoading(false));
  };

  const actionBodyTemplate = (rowData) => {
    return (
      <React.Fragment>
        {canViewRelationTarget(rowData) &&
          <Button icon="pi pi-eye" className=" p-button p-mr-2"
                  tooltip={t('view')} tooltipOptions={{position: 'left'}}
                  // disabled={!rowData?.related_receipt?.id}
                  onClick={() => {
                    if (rowData?.related_receipt?.id) {
                      history.push('/finance/receipt/read/' + rowData.related_receipt.id);
                    } else if (rowData?.inventory_document?.id) {
                      history.push(
                        '/finance/receipt/read/' +
                        receiptId +
                        '?read=inventorydocument&inventoryDocumentId=' +
                          rowData.inventory_document.id
                      );
                    } else if (rowData?.project?.id) {
                      history.push('/project/project/read/' + rowData.project.id);
                    }
                  }}/>
        }
        {localStorage.getItem('roles').toString().includes('role_') && !rowData?.system_relation &&
          <Button icon="pi pi-trash" className="p-button-danger"
                  tooltip={t('delete')} tooltipOptions={{position: 'left'}}
                  onClick={() => {
                    if (window.confirm('A törlés megerősítésére van szükség!')) {
                      setLoading(true);
                      apiCall('delete', '/api/v2/receipt-relation/' + rowData.id)
                        .then(response => {
                          window.App.toastShow('success', 'Sikeres törlés!');
                          window.App.setState({rerenderReceiptRelationList: Math.random()})
                        })
                        .catch(error => {
                          window.App.toastShow('error', t('error'), error.toString(), '', error.response);
                        })
                        .finally(() => {
                          setLoading(false);
                        })
                    }
                  }}
          />
        }
      </React.Fragment>
    );
  }

  useEffect(() => {
    const vars = window.App.urlVars ? window.App.urlVars() : {};
    const rid = vars?.related_receipt ? Number(vars.related_receipt) : null;
    const did = vars?.inventory_document ? Number(vars.inventory_document) : null;
    if (!rid && !did) return;
    const loadPickedLabels = async () => {
      try {
        if (rid) {
          const res = await apiCall('get', `/api/v2/receipt/${rid}`);
          const item = res?.data?.data || res?.data;
          setSelectedReceipt({
            id: rid,
            label: item?.receipt_number || item?.number || `#${rid}`,
          });
        }
        if (did) {
          const res = await apiCall('get', `/api/v2/inventory-document/${did}`);
          const item = res?.data?.data || res?.data;
          setSelectedInvDoc({
            id: did,
            label: item?.document_number || item?.number || `#${did}`,
          });
        }
      } catch (e) {
        if (rid) setSelectedReceipt({ id: rid, label: `#${rid}` });
        if (did) setSelectedInvDoc({ id: did, label: `#${did}` });
      } finally {
        const basePath = history.location.pathname;
        const params = new URLSearchParams(history.location.search);
        params.delete('search');
        params.delete('related_receipt');
        params.delete('inventory_document');

        const qs = params.toString();
        history.replace(qs ? `${basePath}?${qs}` : basePath);
      }
    };
    loadPickedLabels();
  }, [history.location.search]);

  useEffect(() => {
    axios({
      method: 'get',
      url: process.env.REACT_APP_API_HOST + "/api/v2/dictionary/receipt-type",
      params: {},
      headers: {
        'Authorization': 'Bearer ' + localStorage.getItem("userToken"),
        'App-Network': localStorage.getItem("networkId")
      }
    })
      .then(response => {
        const raw = response?.data?.data || [];
        const types = raw.map(item => ({
          id: item.key,
          name: item.value
        }));
        setReceiptTypesDict(types);
      })
      .catch(error => {
        window.App.toastShow('error', t('error'), error.toString(), '', error.response);
      });
    axios({
      method: 'get',
      url: process.env.REACT_APP_API_HOST + "/api/v2/dictionary/inventory-document-type",
      params: {},
      headers: {
        'Authorization': 'Bearer ' + localStorage.getItem("userToken"),
        'App-Network': localStorage.getItem("networkId")
      }
    })
      .then(response => {
        const raw = response?.data?.data || [];
        const types = raw.map(item => ({
          id: item.key,
          name: item.value
        }));
        setInvDocTypesDict(types);
      })
      .catch(error => {
        window.App.toastShow('error', t('error'), error.toString(), '', error.response);
      });
  }, []);

  useEffect(() => {
    loadLazyData();
  }, [lazyParams, window.App.state.rerenderReceiptRelationList])

  return (
    <>
      <div className="p-grid p-mb-2">
        <div className="p-col-12 p-lg-6 p-col-align-center p-text-center p-text-lg-left">
          <h2 className="p-m-0 p-p-0 text-primary p-d-inline">
            {t('relatedReceipts')}
          </h2>
        </div>
        <div className="p-col-12 p-text-right">
          <Button
              label={t('addInvoice')}
              icon="pi pi-plus"
              className="p-button-success p-mr-2"
              onClick={() => setShowAddReceipt(true)}
            />

          <Button
            label={t('addDeliveryNote')}
            icon="pi pi-plus"
            className="p-button-success"
            onClick={() => setShowAddInvDoc(true)}
          />
        </div>
      </div>

      <Dialog
        header={t('addInvoice')}
        visible={showAddReceipt}
        style={{ width: '650px' }}
        modal
        onHide={() => { setShowAddReceipt(false); setDialogError(null); }}
        footer={
          <div className="p-d-flex p-jc-end">
            <Button
              label={t('save')}
              icon={"pi pi-check"}
              className="p-button-success p-ml-2"
              loading={createLoading}
              disabled={!selectedReceipt?.id}
              onClick={() => createRelation('receipt')}
            />
          </div>
        }
      >
        <div className="p-fluid">
          <label className="p-d-block p-mb-2">{t('fieldBrowseInvoice')}</label>
          <div className="p-inputgroup">
            <InputText
              value={selectedReceipt?.label || ''}
              placeholder={t('noneSelected')}
              readOnly
            />
            <Button
              type="button"
              icon="pi pi-search"
              onClick={() => openBrowse('receipt')}
            />
          </div>
          {dialogError && (
            <pre className="p-mt-3" style={{ whiteSpace: 'pre-wrap', color: 'var(--red-500)' }}>
              {dialogError}
            </pre>
          )}
        </div>
      </Dialog>
      <Dialog
        header={t('addDeliveryNote')}
        visible={showAddInvDoc}
        style={{ width: '650px' }}
        modal
        onHide={() => { setShowAddInvDoc(false); setDialogError(null); }}
        footer={
          <div className="p-d-flex p-jc-end">
            <Button
              label={t('save')}
              icon={"pi pi-check"}
              className="p-button-success p-ml-2"
              loading={createLoading}
              disabled={!selectedInvDoc?.id}
              onClick={() => createRelation('invdoc')}
            />
          </div>
        }
      >
        <div className="p-fluid">
          <label className="p-d-block p-mb-2">{t('fieldBrowseDeliveryNote')}</label>
          <div className="p-inputgroup">
            <InputText
              value={selectedInvDoc?.label || ''}
              placeholder={t('noneSelected')}
              readOnly
            />
            <Button
              type="button"
              icon="pi pi-search"
              onClick={() => openBrowse('invdoc')}
            />
          </div>
          {dialogError && (
            <pre className="p-mt-3" style={{ whiteSpace: 'pre-wrap', color: 'var(--red-500)' }}>
              {dialogError}
            </pre>
          )}
        </div>
      </Dialog>

      <DataTable size={localStorage.getItem('scale')}
                 emptyMessage={t('noResults')}
                 value={receiptRelations} paginator first={lazyParams.first} totalRecords={totalRecords} onPage={onPage}
                 onSort={onSort} sortField={lazyParams.sortField} sortOrder={lazyParams.sortOrder}
                 onFilter={onFilter} filters={lazyParams.filters} loading={loading} lazy
                 paginatorTemplate={pager} rows={rows}
                 autoLayout={true}
                 filterDisplay="row"
                 responsiveLayout="scroll"
                 stripedRows
                 ref={dt}
                 dataKey="id"
      >
        <Column header={t('columnDocumentNumber2')}
          body={(rowData) => (
            <span>
              {rowData?.inventory_document?.document_number
                || rowData?.related_receipt?.receipt_number
                || rowData?.project?.project_number
                || '-'}
            </span>
          )}
        />

        <Column header={t('columnType')}
          body={(rowData) => {
            if (rowData?.inventory_document?.type) {
              return <span>{translateInvDocType(rowData.inventory_document.type)}</span>;
            }
            if (rowData?.related_receipt?.receipt_type) {
              return <span>{translateReceiptType(rowData.related_receipt.receipt_type)}</span>;
            }
            if (rowData?.project?.name) {
              return <span>{rowData.project.name}</span>;
            }
            return <span>-</span>;
          }}
        />

        <Column header={t('columnRelation')}
          body={(rowData) => (
            <div>
              <div className="p-text-bold">{t(`receiptRelationType.${rowData.type || 'MANUAL_RELATED_RECEIPT'}`)}</div>
              <small className="text-secondary">{translateRelationDescription(rowData)}</small>
            </div>
          )}
        />

        <Column header={t('columnReceiptGroup')}
          body={(rowData) => rowData?.related_receipt?.receipt_group_name || '-'}
        />

        <Column header={t('columnStatus')}
          body={(rowData) => rowData?.related_receipt
            ? (rowData.related_receipt.draft ? t('draft') : t('finalized'))
            : '-'}
        />

        <Column header={t('columnGrossAmount')}
          body={(rowData) => rowData?.related_receipt
            ? `${new Intl.NumberFormat(undefined, {maximumFractionDigits: 2}).format(Number(rowData.related_receipt.gross_amount || 0))} ${rowData.related_receipt.currency || ''}`
            : '-'}
        />

        <Column body={actionBodyTemplate} className={"p-py-0 p-text-right p-text-nowrap"} />
      </DataTable>
    </>
  )
}
export default withTranslation('common')(ReceiptRelationListComponent);
